import React, { memo, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Text, Title } from 'app/pages/TicketRefund/components/Typo';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import StyledTable from 'app/components/StyledTable';
import TextCell from 'app/pages/TicketRefund/components/TextCell';
import { formatNumber, getFromDateToDate } from 'utils/helper';
import { renderTicketDetail } from '../../../TicketSelectedList';
import useScrollIntoElement from 'utils/hooks/useScrollIntoElement';

interface GiftItemsProps {
  giftItemsData: any;
}

const GiftItems = (props: GiftItemsProps) => {
  const { giftItemsData } = props;
  const translations = useSelector(selectTranslations);

  const anchorPoint = useRef<HTMLDivElement>(null);
  const { onScrollIntoElement } = useScrollIntoElement({
    elementRef: anchorPoint,
  });

  const columns = useMemo(
    () => [
      {
        title: (
          <span style={{ paddingLeft: 8 }}>
            {translations?.['b2b.txt.ticketName'] ?? ''}
          </span>
        ),
        dataIndex: 'ticketName',
        key: 'ticketName',
        width: 125,
      },
      {
        title: translations?.['b2b.txt.ticketPark'] ?? '',
        dataIndex: 'ticketPark',
        key: 'ticketPark',
        width: 200,
      },
      {
        title: translations?.['b2b.txt.ticketType'] ?? '',
        dataIndex: 'ticketType',
        key: 'ticketType',
        width: 100,
      },
      {
        title: translations?.['b2b.txt.ticket-usage-from'] ?? '',
        dataIndex: 'usageDate',
        key: 'usageDate',
        width: 116,
      },
      {
        title: translations?.['b2b.txt.ticket-usage-to'] ?? '',
        dataIndex: 'ticketExpired',
        key: 'ticketExpired',
        width: 116,
      },
      {
        title: translations?.['b2b.txt.ticket-detail'] ?? '',
        dataIndex: 'ticketDetail',
        key: 'ticketDetail',
        width: 103,
      },
      {
        title: translations?.['b2b.refund.serial'] ?? '',
        dataIndex: 'serial',
        key: 'serial',
        width: 116,
      },
      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.refund.discount'] ?? ''}
          </span>
        ),
        dataIndex: 'discount',
        key: 'discount',
        width: 116,
      },

      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.refund.totalPrice'] ?? ''}
          </span>
        ),
        dataIndex: 'totalPrice',
        key: 'totalPrice',
        width: 116,
      },
    ],
    [translations],
  );

  const totalTicketAmount = useRef(0);

  const dataSource = useMemo(() => {
    totalTicketAmount.current = 0;
    return giftItemsData?.map((ticket, idx) => {
      totalTicketAmount.current =
        totalTicketAmount.current + (ticket?.amount ?? 0);

      const { fromDate, toDate } = getFromDateToDate({
        catalog: { ...ticket?.catalog },
        date: { ...ticket?.dateFrom },
        show: { ...ticket?.show },
        product: { ...ticket?.product },
      });

      return {
        key: idx,
        ticketName: (
          <TextCell style={{ paddingLeft: 8 }}>
            {ticket?.product?.name}
          </TextCell>
        ),
        ticketPark: <TextCell>{ticket?.park?.name}</TextCell>,
        ticketType: <TextCell>{ticket?.catalog?.name}</TextCell>,
        usageDate: <TextCell>{fromDate}</TextCell>,
        ticketExpired: <TextCell>{toDate}</TextCell>,
        ticketDetail: (
          <TextCell>
            {renderTicketDetail([
              ticket?.product?.restaurant?.name,
              ticket?.ZoneName,
              ticket?.PerformanceTime,
            ])}
          </TextCell>
        ),
        serial: (
          <TextCell style={{ textAlign: 'right' }}>
            {(
              ticket?.mediaCode ||
              ticket?.verifyCode ||
              ticket?.ticketCode ||
              ''
            )
              .split(',')
              .map((code, index) => (
                <div key={index}>{code}</div>
              ))}
          </TextCell>
        ),
        discount: (
          <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
            ticket?.discountAmount ?? 0,
          ).replaceAll(',', '.')}đ`}</TextCell>
        ),
        totalPrice: (
          <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
            ticket?.totalPrice ?? 0,
          ).replaceAll(',', '.')}đ`}</TextCell>
        ),
      };
    });
  }, [giftItemsData]);

  return (
    <div style={{ marginBottom: 24 }}>
      <div>
        <Title style={{ textTransform: 'initial', marginBottom: 16 }}>
          {translations?.['b2b.refund.order-gift'] ?? ''} -{' '}
          {translations?.['b2b.totalTicketAmount']?.replace(
            '${amount}',
            totalTicketAmount.current,
          ) ?? ''}
        </Title>
      </div>
      <div ref={anchorPoint} />
      <StyledTable
        columns={columns}
        dataSource={dataSource}
        styleSlots={{
          th: {
            fontWeight: 600,
            fontSize: 14,
            textAlign: 'left',
          },
        }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: onScrollIntoElement,
        }}
      />
    </div>
  );
};

export default memo(GiftItems);
